/**
 * Exercise Generator Engine
 * -------------------------
 * Generates musically correct sight-reading exercises.
 *
 * Key principles:
 * - Every measure fills EXACTLY to the time signature (no overflow/underflow)
 * - Notes follow conventional stepwise motion with occasional leaps
 * - Each note carries its beat position and expected duration in ms (for timing eval)
 * - Stem direction is determined by note position relative to B4 (treble) / D3 (bass)
 */

export type Clef = "treble" | "bass" | "both";
export type KeySignature = "C" | "G" | "D" | "A" | "E" | "F" | "Bb" | "Eb" | "Ab";
export type TimeSignature = "4/4" | "3/4" | "2/4" | "6/8";
export type Difficulty = 1 | 2 | 3 | 4 | 5;
export type KeyboardRange = "full" | "61-key" | "49-key" | "37-key" | "25-key";

export interface ExerciseNote {
  midi: number;
  duration: string;        // VexFlow duration: "q" = quarter, "h" = half, "w" = whole, "8" = eighth
  beats: number;           // How many beats this note occupies
  dotted: boolean;
  rest: boolean;
  accidental?: string;     // "#", "b", "n"
  beatPosition: number;    // Which beat this note starts on (0-indexed within measure)
  globalBeatPosition: number; // Beat position from the start of the exercise
  expectedDurationMs: number; // How long the note should be held (computed from tempo)
  tieStart?: boolean;      // This note is tied to the next note of the same pitch
  tieStop?: boolean;       // This note continues the previous note (play as one held note)
}

export interface ExerciseMeasure {
  notes: ExerciseNote[];
  clef: "treble" | "bass";
  measureIndex: number;
}

export interface Exercise {
  id: string;
  measures: ExerciseMeasure[];
  clef: Clef;
  keySignature: KeySignature;
  timeSignature: TimeSignature;
  difficulty: Difficulty;
  tempo: number;
  totalNotes: number;       // playable (non-rest) notes
  totalBeats: number;       // total beats in the exercise
  beatDurationMs: number;   // milliseconds per beat at this tempo
}

// ─── Note ranges per clef and difficulty ───────────────────────────
// Staff-anchored, modelled on ABRSM sight-reading tests: even at the top
// difficulty notes stay within ~2 ledger lines of the staff. Difficulty maps
// roughly to ABRSM grades: 1≈G1-2, 2≈G3, 3≈G4-5, 4≈G6, 5≈G7-8.

const TREBLE_RANGES: Record<Difficulty, [number, number]> = {
  1: [60, 76],  // C4–E5 (5-finger positions sit inside this)
  2: [60, 77],  // C4–F5 (staff plus middle C)
  3: [59, 79],  // B3–G5
  4: [57, 81],  // A3–A5 (≤2 ledger lines either side)
  5: [55, 81],  // G3–A5
};

const BASS_RANGES: Record<Difficulty, [number, number]> = {
  1: [45, 60],  // A2–C4
  2: [43, 60],  // G2–C4 (staff plus middle C)
  3: [41, 62],  // F2–D4
  4: [40, 64],  // E2–E4 (≤2 ledger lines either side)
  5: [38, 64],  // D2–E4
};

/** How many consecutive scale degrees the "hand position" spans per difficulty.
 *  ABRSM grades 1-2 stay in a 5-finger position; higher grades move out of it. */
const HAND_SPAN_DEGREES: Record<Difficulty, number> = {
  1: 5,   // classic 5-finger position
  2: 8,   // one octave
  3: 10,
  4: 12,
  5: 14,
};

/** Pitch class of each key's tonic (hand positions are anchored on the tonic). */
const KEY_TONIC_PC: Record<KeySignature, number> = {
  C: 0, G: 7, D: 2, A: 9, E: 4, F: 5, Bb: 10, Eb: 3, Ab: 8,
};

// ─── Keyboard range constraints ────────────────────────────────────

const KEYBOARD_CONSTRAINTS: Record<KeyboardRange, [number, number]> = {
  "full": [21, 108],    // 88-key piano (A0 to C8)
  "61-key": [36, 96],   // 61-key keyboard (C2 to C7)
  "49-key": [36, 84],   // 49-key keyboard (C2 to C6)
  "37-key": [48, 84],   // 37-key keyboard (C3 to C6)
  "25-key": [60, 84],   // 25-key keyboard (C4 to C6)
};

function constrainRange(range: [number, number], constraint: [number, number]): [number, number] {
  return [
    Math.max(range[0], constraint[0]),
    Math.min(range[1], constraint[1])
  ];
}

// ─── Key signature scales ──────────────────────────────────────────

const KEY_SCALES: Record<KeySignature, number[]> = {
  C:  [0, 2, 4, 5, 7, 9, 11],
  G:  [0, 2, 4, 6, 7, 9, 11],
  D:  [1, 2, 4, 6, 7, 9, 11],
  A:  [1, 2, 4, 6, 8, 9, 11],
  E:  [1, 3, 4, 6, 8, 9, 11],
  F:  [0, 2, 4, 5, 7, 9, 10],
  Bb: [0, 2, 3, 5, 7, 9, 10],
  Eb: [0, 2, 3, 5, 7, 8, 10],
  Ab: [0, 1, 3, 5, 7, 8, 10],
};

// ─── Rhythm patterns ────────────────────────────────────────────────
// Whole-measure rhythm cells (values in beats; eighth-note units for 6/8),
// in the style of ABRSM tests: each measure is a coherent rhythmic figure
// rather than a random fill. Higher difficulties inherit lower patterns.

const SIMPLE_PATTERNS: Record<Exclude<TimeSignature, "6/8">, Record<Difficulty, number[][]>> = (() => {
  const p44: Record<Difficulty, number[][]> = {
    1: [[4], [2, 2], [2, 1, 1], [1, 1, 2], [1, 1, 1, 1], [3, 1]],
    2: [[1, 1, 1, 0.5, 0.5], [2, 0.5, 0.5, 1], [0.5, 0.5, 1, 2], [1.5, 0.5, 1, 1], [2, 1.5, 0.5]],
    3: [[1.5, 0.5, 1.5, 0.5], [1, 0.5, 0.5, 1, 1], [0.5, 0.5, 0.5, 0.5, 2], [3, 0.5, 0.5]],
    4: [[0.5, 0.5, 0.5, 0.5, 1, 1], [1.5, 0.5, 0.5, 0.5, 1], [1, 0.5, 0.5, 0.5, 0.5, 1]],
    5: [[0.5, 1, 0.5, 2], [1, 0.5, 1, 0.5, 1], [0.5, 1, 1, 0.5, 1]],
  };
  const p34: Record<Difficulty, number[][]> = {
    1: [[3], [2, 1], [1, 2], [1, 1, 1]],
    2: [[1, 1, 0.5, 0.5], [1.5, 0.5, 1], [1, 0.5, 0.5, 1]],
    3: [[0.5, 0.5, 1, 1], [2, 0.5, 0.5], [1.5, 0.5, 0.5, 0.5]],
    4: [[0.5, 0.5, 0.5, 0.5, 1], [1, 0.5, 0.5, 0.5, 0.5]],
    5: [[0.5, 1, 0.5, 1], [0.5, 1, 1, 0.5]],
  };
  const p24: Record<Difficulty, number[][]> = {
    1: [[2], [1, 1]],
    2: [[1, 0.5, 0.5], [0.5, 0.5, 1], [1.5, 0.5]],
    3: [[0.5, 0.5, 0.5, 0.5]],
    4: [[0.5, 0.5, 1]],
    5: [[0.5, 1, 0.5]],
  };
  return { "4/4": p44, "3/4": p34, "2/4": p24 };
})();

/** 6/8 patterns in eighth-note units (introduced at ABRSM grade 4 ≈ difficulty 3). */
const COMPOUND_PATTERNS: Record<Difficulty, number[][]> = {
  1: [[3, 3], [6]],
  2: [[3, 3], [6], [2, 1, 3]],
  3: [[2, 1, 3], [3, 2, 1], [1, 1, 1, 3], [6]],
  4: [[2, 1, 2, 1], [1, 1, 1, 1, 1, 1], [3, 1, 1, 1]],
  5: [[2, 2, 2], [1, 1, 1, 2, 1]], // includes hemiola
};

/** Cumulative pattern pool: difficulty d draws from grades 1..d. */
function patternsFor(timeSignature: TimeSignature, difficulty: Difficulty): number[][] {
  const table = timeSignature === "6/8" ? COMPOUND_PATTERNS : SIMPLE_PATTERNS[timeSignature];
  const pool: number[][] = [];
  for (let d = 1 as Difficulty; d <= difficulty; d++) {
    pool.push(...table[d as Difficulty]);
  }
  return pool;
}

/** Map a beat value to VexFlow notation. Values must come from the pattern tables. */
function beatsToNotation(beats: number, is68: boolean): { duration: string; dotted: boolean } {
  if (is68) {
    if (beats === 6) return { duration: "h", dotted: true };
    if (beats === 4) return { duration: "h", dotted: false };
    if (beats === 3) return { duration: "q", dotted: true };
    if (beats === 2) return { duration: "q", dotted: false };
    return { duration: "8", dotted: false };
  }
  if (beats === 4) return { duration: "w", dotted: false };
  if (beats === 3) return { duration: "h", dotted: true };
  if (beats === 2) return { duration: "h", dotted: false };
  if (beats === 1.5) return { duration: "q", dotted: true };
  if (beats === 1) return { duration: "q", dotted: false };
  return { duration: "8", dotted: false };
}

// ─── Tempo ranges ──────────────────────────────────────────────────

const TEMPO_RANGES: Record<Difficulty, [number, number]> = {
  1: [60, 72],
  2: [66, 84],
  3: [76, 96],
  4: [84, 108],
  5: [96, 126],
};

// ─── Helpers ───────────────────────────────────────────────────────

function getBeatsPerMeasure(timeSig: TimeSignature): number {
  switch (timeSig) {
    case "4/4": return 4;
    case "3/4": return 3;
    case "2/4": return 2;
    case "6/8": return 6; // 6 eighth notes total (compound meter)
    default: return 4;
  }
}

function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function randomChoice<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function weightedChoice<T>(items: T[], weights: number[]): T {
  const total = weights.reduce((a, b) => a + b, 0);
  let r = Math.random() * total;
  for (let i = 0; i < items.length; i++) {
    r -= weights[i];
    if (r <= 0) return items[i];
  }
  return items[items.length - 1];
}

function getScaleNotes(keySignature: KeySignature, range: [number, number]): number[] {
  const scale = KEY_SCALES[keySignature];
  const notes: number[] = [];
  for (let midi = range[0]; midi <= range[1]; midi++) {
    if (scale.includes(midi % 12)) {
      notes.push(midi);
    }
  }
  return notes;
}

// ─── Melodic engine ────────────────────────────────────────────────
// Works in scale-degree space inside a tonic-anchored hand-position window.
// Step motion dominates; leaps are bounded by difficulty and resolve by step
// in the opposite direction (classic gap-fill), as in ABRSM melodies.

interface MelodyState {
  /** Index into scaleNotes of the previous note */
  idx: number;
  /** Signed size (in scale degrees) of the last interval, to resolve leaps */
  lastInterval: number;
  /** Inclusive window bounds (indices into scaleNotes) */
  windowLo: number;
  windowHi: number;
}

function nextMelodyIndex(state: MelodyState, difficulty: Difficulty): number {
  const { idx, lastInterval, windowLo, windowHi } = state;

  // After a leap (a 4th or more), resolve by step in the opposite direction.
  if (Math.abs(lastInterval) >= 3) {
    const back = idx - Math.sign(lastInterval);
    if (back >= windowLo && back <= windowHi) return back;
  }

  const maxLeap = difficulty <= 2 ? 2 : difficulty <= 4 ? 3 : 4;
  const candidates: number[] = [];
  const weights: number[] = [];
  for (let interval = -maxLeap; interval <= maxLeap; interval++) {
    const target = idx + interval;
    if (target < windowLo || target > windowHi) continue;
    const dist = Math.abs(interval);
    // Steps strongly preferred, thirds common, larger leaps rare, repeats rarer.
    const w = dist === 1 ? 10 : dist === 2 ? 4 : dist === 0 ? 1.5 : 1;
    // Gentle gravity toward the window centre so lines arch instead of drifting.
    const centre = (windowLo + windowHi) / 2;
    const drift = Math.abs(target - centre) > Math.abs(idx - centre) ? 0.7 : 1;
    candidates.push(target);
    weights.push(w * drift);
  }
  if (candidates.length === 0) return idx;

  return weightedChoice(candidates, weights);
}

/** Realize a rhythm pattern as notes, advancing the melody state. */
function realizePattern(
  pattern: number[],
  scaleNotes: number[],
  state: MelodyState,
  difficulty: Difficulty,
  measureIndex: number,
  globalBeatOffset: number,
  beatDurationMs: number,
  is68: boolean,
): ExerciseNote[] {
  // Occasionally end a middle measure with a rest (ABRSM uses rests sparingly,
  // at phrase ends) — never in the first measure and only for beats ≥ 1 unit.
  const lastBeats = pattern[pattern.length - 1];
  const restLast =
    difficulty >= 2 &&
    measureIndex > 0 &&
    pattern.length > 1 &&
    lastBeats >= (is68 ? 2 : 1) &&
    Math.random() < 0.12;

  const notes: ExerciseNote[] = [];
  let localBeat = 0;

  pattern.forEach((beats, i) => {
    const isRest = restLast && i === pattern.length - 1;
    let midi = 60;
    if (!isRest) {
      const nextIdx = nextMelodyIndex(state, difficulty);
      state.lastInterval = nextIdx - state.idx;
      state.idx = nextIdx;
      midi = scaleNotes[nextIdx];
    }

    const notation = beatsToNotation(beats, is68);
    notes.push({
      midi,
      duration: notation.duration,
      beats,
      dotted: notation.dotted,
      rest: isRest,
      beatPosition: localBeat,
      globalBeatPosition: globalBeatOffset + localBeat,
      expectedDurationMs: beats * beatDurationMs,
    });
    localBeat += beats;
  });

  return notes;
}

/** Pick the starting window (hand position) anchored on a tonic. */
function chooseWindow(scaleNotes: number[], keySignature: KeySignature, difficulty: Difficulty): { lo: number; hi: number } {
  const span = Math.min(HAND_SPAN_DEGREES[difficulty], scaleNotes.length);
  const tonicPc = KEY_TONIC_PC[keySignature];
  const tonicIdxs = scaleNotes
    .map((midi, i) => ({ midi, i }))
    .filter(({ midi }) => midi % 12 === tonicPc)
    .map(({ i }) => i);

  // Prefer the tonic closest to the centre of the available range.
  const centre = (scaleNotes.length - 1) / 2;
  const anchor = tonicIdxs.length > 0
    ? tonicIdxs.reduce((best, i) => (Math.abs(i - centre) < Math.abs(best - centre) ? i : best))
    : Math.floor(centre);

  // At difficulty 1 the window starts on the tonic (5-finger position);
  // otherwise the tonic sits in the lower third so lines can rise and fall.
  let lo = difficulty === 1 ? anchor : anchor - Math.floor(span / 3);
  lo = Math.max(0, Math.min(lo, scaleNotes.length - span));
  return { lo, hi: lo + span - 1 };
}

/** Index (within the window) of the tonic the melody should cadence onto. */
function cadenceTonicIndex(scaleNotes: number[], state: MelodyState, keySignature: KeySignature): number {
  const tonicPc = KEY_TONIC_PC[keySignature];
  let best = -1;
  for (let i = state.windowLo; i <= state.windowHi; i++) {
    if (scaleNotes[i] % 12 !== tonicPc) continue;
    if (best === -1 || Math.abs(i - state.idx) < Math.abs(best - state.idx)) best = i;
  }
  return best !== -1 ? best : state.idx;
}

// ─── Main generator ────────────────────────────────────────────────

export function generateExercise(params: {
  clef: Clef;
  keySignature: KeySignature;
  timeSignature: TimeSignature;
  difficulty: Difficulty;
  measureCount?: number;
  keyboardRange?: KeyboardRange;
  tempo?: number;
}): Exercise {
  const { clef, keySignature, timeSignature, difficulty, measureCount = 4, keyboardRange = "full", tempo: tempoOverride } = params;
  const beatsPerMeasure = getBeatsPerMeasure(timeSignature);
  const [tempoMin, tempoMax] = TEMPO_RANGES[difficulty];
  const tempo = tempoOverride ?? randomInt(tempoMin, tempoMax);
  
  // For 6/8, tempo is in dotted quarter notes (each = 3 eighth notes)
  // For other meters, tempo is in quarter notes
  const is68 = timeSignature === "6/8";
  const beatDurationMs = is68 ? (60000 / tempo) / 3 : (60000 / tempo);

  // Apply keyboard range constraints
  const keyboardConstraint = KEYBOARD_CONSTRAINTS[keyboardRange];
  const patternPool = patternsFor(timeSignature, difficulty);

  // Per-clef melodic context: scale notes, hand-position window, melody state.
  const makeClefContext = (clefRanges: Record<Difficulty, [number, number]>) => {
    const range = constrainRange(clefRanges[difficulty], keyboardConstraint);
    const scaleNotes = getScaleNotes(keySignature, range);
    const window = chooseWindow(scaleNotes, keySignature, difficulty);
    const tonicPc = KEY_TONIC_PC[keySignature];
    // Start on a tonic-chord tone: tonic at low difficulty, tonic/3rd/5th above.
    const chordPcs = difficulty === 1
      ? [tonicPc]
      : [tonicPc, (tonicPc + 4) % 12, (tonicPc + 7) % 12];
    const startCandidates: number[] = [];
    for (let i = window.lo; i <= window.hi; i++) {
      if (chordPcs.includes(scaleNotes[i] % 12)) startCandidates.push(i);
    }
    const state: MelodyState = {
      idx: startCandidates.length > 0 ? randomChoice(startCandidates) : window.lo,
      lastInterval: 0,
      windowLo: window.lo,
      windowHi: window.hi,
    };
    return { scaleNotes, state };
  };

  const trebleCtx = clef === "treble" || clef === "both" ? makeClefContext(TREBLE_RANGES) : null;
  const bassCtx = clef === "bass" || clef === "both" ? makeClefContext(BASS_RANGES) : null;

  const measures: ExerciseMeasure[] = [];
  let totalNotes = 0;
  let globalBeatOffset = 0;

  const generateMeasure = (
    ctx: { scaleNotes: number[]; state: MelodyState },
    measureIndex: number
  ): ExerciseNote[] => {
    const isFinal = measureIndex === measureCount - 1;
    const isPenultimate = measureIndex === measureCount - 2;

    // From grade ≈4 up, shift the hand position a few degrees at the halfway
    // point (ABRSM moves out of fixed positions at the middle grades).
    if (difficulty >= 3 && measureIndex === Math.floor(measureCount / 2)) {
      const shift = randomChoice([-2, 2, 3]);
      const span = ctx.state.windowHi - ctx.state.windowLo;
      let lo = Math.max(0, Math.min(ctx.state.windowLo + shift, ctx.scaleNotes.length - 1 - span));
      ctx.state.windowLo = lo;
      ctx.state.windowHi = lo + span;
      ctx.state.idx = Math.max(lo, Math.min(ctx.state.idx, lo + span));
    }

    // Final bar: a single long tonic (the standard ABRSM cadential ending).
    if (isFinal) {
      const tonicIdx = cadenceTonicIndex(ctx.scaleNotes, ctx.state, keySignature);
      ctx.state.lastInterval = tonicIdx - ctx.state.idx;
      ctx.state.idx = tonicIdx;
      const notation = beatsToNotation(beatsPerMeasure, is68);
      return [{
        midi: ctx.scaleNotes[tonicIdx],
        duration: notation.duration,
        beats: beatsPerMeasure,
        dotted: notation.dotted,
        rest: false,
        beatPosition: 0,
        globalBeatPosition: globalBeatOffset,
        expectedDurationMs: beatsPerMeasure * beatDurationMs,
      }];
    }

    const pattern = randomChoice(patternPool);
    const notes = realizePattern(
      pattern, ctx.scaleNotes, ctx.state, difficulty,
      measureIndex, globalBeatOffset, beatDurationMs, is68
    );

    // Approach the cadence by step: the penultimate bar ends on a neighbour
    // of the tonic the final bar will land on.
    if (isPenultimate) {
      const tonicIdx = cadenceTonicIndex(ctx.scaleNotes, ctx.state, keySignature);
      const neighbour = ctx.state.idx > tonicIdx ? tonicIdx + 1 : tonicIdx - 1;
      const clamped = Math.max(ctx.state.windowLo, Math.min(neighbour, ctx.state.windowHi));
      for (let i = notes.length - 1; i >= 0; i--) {
        if (!notes[i].rest) {
          notes[i].midi = ctx.scaleNotes[clamped];
          ctx.state.lastInterval = clamped - ctx.state.idx;
          ctx.state.idx = clamped;
          break;
        }
      }
    }

    return notes;
  };

  for (let i = 0; i < measureCount; i++) {
    if (trebleCtx) {
      const notes = generateMeasure(trebleCtx, i);
      totalNotes += notes.filter(n => !n.rest).length;
      measures.push({ notes, clef: "treble", measureIndex: i });
    }

    if (bassCtx) {
      const notes = generateMeasure(bassCtx, i);
      totalNotes += notes.filter(n => !n.rest).length;
      measures.push({ notes, clef: "bass", measureIndex: i });
    }

    globalBeatOffset += beatsPerMeasure;
  }

  return {
    id: `ex-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    measures,
    clef,
    keySignature,
    timeSignature,
    difficulty,
    tempo,
    totalNotes,
    totalBeats: globalBeatOffset,
    beatDurationMs,
  };
}

// ─── VexFlow helpers ───────────────────────────────────────────────

export function getVexFlowKeySignature(key: KeySignature): string {
  return key; // VexFlow accepts the same strings
}

/** Convert MIDI note to VexFlow key string using enharmonic spelling for key context */
export function midiToVexFlowKey(midi: number, keySignature?: KeySignature): string {
  // Use flats for flat keys, sharps for sharp keys
  const flatKeys: KeySignature[] = ["F", "Bb", "Eb", "Ab"];
  const useFlats = keySignature && flatKeys.includes(keySignature);

  const sharpNames = ["c", "c#", "d", "d#", "e", "f", "f#", "g", "g#", "a", "a#", "b"];
  const flatNames  = ["c", "db", "d", "eb", "e", "f", "gb", "g", "ab", "a", "bb", "b"];

  const names = useFlats ? flatNames : sharpNames;
  const octave = Math.floor(midi / 12) - 1;
  const name = names[midi % 12];
  return `${name}/${octave}`;
}

/** Letters altered by each key signature (sharp keys raise, flat keys lower) */
const KEY_ALTERATIONS: Record<KeySignature, { letters: string[]; type: "#" | "b" | null }> = {
  C:  { letters: [], type: null },
  G:  { letters: ["f"], type: "#" },
  D:  { letters: ["f", "c"], type: "#" },
  A:  { letters: ["f", "c", "g"], type: "#" },
  E:  { letters: ["f", "c", "g", "d"], type: "#" },
  F:  { letters: ["b"], type: "b" },
  Bb: { letters: ["b", "e"], type: "b" },
  Eb: { letters: ["b", "e", "a"], type: "b" },
  Ab: { letters: ["b", "e", "a", "d"], type: "b" },
};

/**
 * Returns the accidental that must be drawn explicitly for a note, given the
 * key signature: null when the key signature already implies it, "n" when the
 * note contradicts the key signature, "#"/"b" for chromatic notes outside it.
 * `vexKeyName` is the lowercase note name part of a VexFlow key, e.g. "f#" or "bb".
 */
export function getExplicitAccidental(vexKeyName: string, keySignature: KeySignature): "#" | "b" | "n" | null {
  const letter = vexKeyName[0];
  const accidental = vexKeyName.length > 1 ? (vexKeyName[1] as "#" | "b") : null;
  const keyAlt = KEY_ALTERATIONS[keySignature];
  const letterInKey = keyAlt.letters.includes(letter);

  if (accidental === null) {
    // Natural note: needs an explicit natural only if the key signature alters this letter
    return letterInKey ? "n" : null;
  }
  // Sharp/flat note: implied when the key signature applies the same alteration
  if (letterInKey && keyAlt.type === accidental) return null;
  return accidental;
}

/** Determine stem direction: up if note is below middle line, down if above */
export function getStemDirection(midi: number, clef: "treble" | "bass"): number {
  // VexFlow: 1 = up, -1 = down
  // Treble: B4 (midi 71) is the middle line
  // Bass: D3 (midi 50) is the middle line
  const midLine = clef === "treble" ? 71 : 50;
  return midi < midLine ? 1 : -1;
}
